// Draws the world: four quad layers sharing one atlas, an orthographic camera
// snapped to whole screen pixels, and a day/night tint uniform.
//
// Coordinates: gameplay works in tile space where +y points down the map, the
// same as the tile array. World space flips it (worldY = -tileY) so the camera
// keeps a conventional +y up. Depth is derived from tile y, which makes the
// depth buffer do all sprite sorting for us — the ground, prop and actor layers
// are alpha-tested, so there is no blending order to get wrong.
//
// The fourth layer is the exception. It is blended, it never writes depth, and
// PlayCanvas draws it after every opaque layer because it has a blend mode at
// all. Shadows, particles, weather and lamp glow live there and nowhere else.
import * as pc from "playcanvas";
import { Atlas, type Frame } from "./atlas.ts";
import { QuadBatch, createFxMaterial, createSpriteMaterial } from "./batch.ts";
import { TILE_PX } from "../vendor/spritelib.ts";
import { Prop, World } from "../game/world.ts";

const Z_GROUND = -20;
const Z_STEP = 0.01;
/** in front of the whole map, for full-screen weather */
const Z_WEATHER = 40;
/** how many tiles tall the view aims to be */
const VIEW_TILES = 13;

export type CursorKind = "plain" | "hand" | "blocked";

const CURSOR_SPRITE: Record<CursorKind, string> = {
  plain: "ui_cursor",
  hand: "ui_cursor_hand",
  blocked: "ui_cursor_blocked",
};

export interface ActorOptions {
  tint?: readonly [number, number, number];
  glow?: number;
  /** override the depth, which otherwise comes from baseY */
  z?: number;
  /** lift the sprite off its tile without changing its sort order */
  hop?: number;
}

export class Scene {
  readonly camera: pc.Entity;
  private readonly material: pc.ShaderMaterial;
  private readonly fxMaterial: pc.ShaderMaterial;
  private readonly ground: QuadBatch;
  private readonly props: QuadBatch;
  private readonly actors: QuadBatch;
  private readonly fx: QuadBatch;
  private groundDirty = true;
  private propsDirty = true;
  private pixelScale = 3;
  private camX = 0;
  private camY = 0;

  constructor(
    private readonly app: pc.AppBase,
    private readonly atlas: Atlas,
    private readonly world: World,
  ) {
    this.material = createSpriteMaterial(atlas.texture);
    this.fxMaterial = createFxMaterial(atlas.texture);

    const tiles = world.w * world.h;
    this.ground = new QuadBatch(app, this.material, tiles, "ground");
    this.props = new QuadBatch(app, this.material, tiles * 2, "props");
    this.actors = new QuadBatch(app, this.material, 512, "actors");
    this.fx = new QuadBatch(app, this.fxMaterial, 2048, "fx");
    app.root.addChild(this.ground.entity);
    app.root.addChild(this.props.entity);
    app.root.addChild(this.actors.entity);
    app.root.addChild(this.fx.entity);

    this.camera = new pc.Entity("camera");
    this.camera.addComponent("camera", {
      projection: pc.PROJECTION_ORTHOGRAPHIC,
      orthoHeight: 6,
      nearClip: 0.1,
      farClip: 500,
      clearColor: new pc.Color(0.07, 0.11, 0.09),
    });
    this.camera.setPosition(0, 0, 100);
    app.root.addChild(this.camera);

    this.resize();
    app.graphicsDevice.on("resizecanvas", () => this.resize());
  }

  markGroundDirty(): void {
    this.groundDirty = true;
  }

  markPropsDirty(): void {
    this.propsDirty = true;
  }

  setDaylight(rgb: readonly [number, number, number]): void {
    const v = rgb as unknown as number[];
    this.material.setParameter("uDaylight", v);
    this.fxMaterial.setParameter("uDaylight", v);
  }

  setClearColor(r: number, g: number, b: number): void {
    this.camera.camera!.clearColor = new pc.Color(r, g, b);
  }

  /** Whole-screen pixel scale, exposed so the settings panel can override it. */
  setPixelScale(scale: number | null): void {
    this.forcedScale = scale;
    this.resize();
  }

  private forcedScale: number | null = null;

  /** Snap the camera to the player, clamped to the map and to whole pixels. */
  focus(tileX: number, tileY: number): void {
    const cam = this.camera.camera!;
    const halfH = cam.orthoHeight;
    const halfW = (halfH * this.app.graphicsDevice.width) / this.app.graphicsDevice.height;
    const x = clamp(tileX, halfW, this.world.w - halfW);
    const y = clamp(tileY, halfH, this.world.h - halfH);
    const unit = TILE_PX * this.pixelScale;
    this.camX = Math.round(x * unit) / unit;
    this.camY = Math.round(-y * unit) / unit;
    this.camera.setPosition(this.camX, this.camY, 100);
  }

  /** Which tile the cursor is over. Exact for an orthographic camera. */
  tileAtScreen(screenX: number, screenY: number): { x: number; y: number } {
    const rect = this.app.graphicsDevice.canvas.getBoundingClientRect();
    const nx = rect.width > 0 ? (screenX - rect.left) / rect.width : 0.5;
    const ny = rect.height > 0 ? (screenY - rect.top) / rect.height : 0.5;
    const halfH = this.camera.camera!.orthoHeight;
    const halfW = (halfH * rect.width) / Math.max(1, rect.height);
    const wx = this.camX + (nx * 2 - 1) * halfW;
    const wy = this.camY - (ny * 2 - 1) * halfH;
    return { x: Math.floor(wx), y: Math.floor(-wy) };
  }

  /** Where a tile-space point lands on screen, for hud text anchored to the world. */
  screenOf(tileX: number, tileY: number): { x: number; y: number } {
    const rect = this.app.graphicsDevice.canvas.getBoundingClientRect();
    const halfH = this.camera.camera!.orthoHeight;
    const halfW = (halfH * rect.width) / Math.max(1, rect.height);
    const nx = (tileX - this.camX) / (halfW * 2) + 0.5;
    const ny = 0.5 - (-tileY - this.camY) / (halfH * 2);
    return { x: rect.left + nx * rect.width, y: rect.top + ny * rect.height };
  }

  /** The tile rectangle currently on screen, padded by a tile on every side. */
  visibleTiles(): { x0: number; y0: number; x1: number; y1: number } {
    const halfH = this.camera.camera!.orthoHeight;
    const halfW = (halfH * this.app.graphicsDevice.width) / this.app.graphicsDevice.height;
    return {
      x0: Math.floor(this.camX - halfW) - 1,
      y0: Math.floor(-this.camY - halfH) - 1,
      x1: Math.ceil(this.camX + halfW) + 1,
      y1: Math.ceil(-this.camY + halfH) + 1,
    };
  }

  // ------------------------------------------------------------ frame build

  beginFrame(): void {
    if (this.groundDirty) {
      this.rebuildGround();
      this.groundDirty = false;
    }
    if (this.propsDirty) {
      this.rebuildProps();
      this.propsDirty = false;
    }
    this.actors.begin();
    this.fx.begin();
  }

  endFrame(): void {
    this.actors.end();
    this.fx.end();
  }

  /** A sprite standing bottom-centre on a tile-space point. */
  actor(spriteId: string, centreX: number, baseY: number, o: ActorOptions = {}): void {
    const f = this.atlas.frame(spriteId);
    const w = f.w / TILE_PX;
    const h = f.h / TILE_PX;
    const z = o.z ?? zFor(baseY);
    this.actors.add(centreX - w / 2, -baseY + (o.hop ?? 0), z, w, h, f, o.tint ?? WHITE, o.glow ?? 0);
  }

  /** A blended sprite centred on a tile-space point. */
  fxSprite(
    spriteId: string,
    centreX: number,
    centreY: number,
    alpha = 1,
    tint: readonly [number, number, number] = WHITE,
    z?: number,
  ): void {
    if (!this.atlas.has(spriteId)) return;
    const f = this.atlas.frame(spriteId);
    const w = f.w / TILE_PX;
    const h = f.h / TILE_PX;
    this.fx.add(centreX - w / 2, -centreY - h / 2, z ?? zFor(centreY) + 0.005, w, h, f, tint, alpha);
  }

  /** The soft ellipse that keeps an actor from looking like a sticker. */
  shadow(centreX: number, baseY: number, scale = 1, alpha = 1): void {
    const f = this.atlas.frame("fx_shadow");
    const w = (f.w / TILE_PX) * scale;
    const h = (f.h / TILE_PX) * scale;
    this.fx.add(centreX - w / 2, -baseY - h * 0.35, zFor(baseY) - 0.002, w, h, f, WHITE, alpha);
  }

  cursorAt(x: number, y: number, kind: CursorKind, pulse: number): void {
    const id = this.atlas.has(CURSOR_SPRITE[kind]) ? CURSOR_SPRITE[kind] : "ui_cursor";
    this.actor(id, x + 0.5, y + 1, { z: zFor(y + 1) + 0.004, glow: kind === "plain" ? pulse : 0 });
  }

  /**
   * Tiles a weather sheet across the whole view. The sheet scrolls by whole
   * texels so the streaks never shimmer against the pixel grid.
   */
  weatherSheet(spriteId: string, scroll: number, alpha: number): void {
    if (!this.atlas.has(spriteId)) return;
    const f = this.atlas.frame(spriteId);
    const v = this.visibleTiles();
    const unit = TILE_PX * this.pixelScale;
    const off = Math.round(scroll * unit) / unit;
    for (let y = v.y0 - 1; y <= v.y1 + 1; y++) {
      for (let x = v.x0; x <= v.x1; x++) {
        this.fx.add(x, -(y + 1) - (off % 1), Z_WEATHER, 1, 1, f, WHITE, alpha);
      }
    }
  }

  /** A single quad stretched over the whole view, for the lightning flash. */
  fullScreenFlash(alpha: number): void {
    if (alpha <= 0) return;
    const f = this.atlas.frame("fx_lightning");
    const v = this.visibleTiles();
    this.fx.add(v.x0, -v.y1, Z_WEATHER + 1, v.x1 - v.x0, v.y1 - v.y0, f, WHITE, alpha);
  }

  get drawnActors(): number {
    return this.actors.length;
  }

  get drawnFx(): number {
    return this.fx.length;
  }

  // ------------------------------------------------------------ layer builds

  private rebuildGround(): void {
    const b = this.ground;
    b.begin();
    for (let y = 0; y < this.world.h; y++) {
      for (let x = 0; x < this.world.w; x++) {
        const f = this.atlas.frame(this.world.groundSprite(x, y));
        b.add(x, -(y + 1), Z_GROUND, 1, 1, f);
      }
    }
    b.end();
  }

  private rebuildProps(): void {
    const b = this.props;
    b.begin();
    for (let y = 0; y < this.world.h; y++) {
      for (let x = 0; x < this.world.w; x++) {
        const crop = this.world.cropAt(x, y);
        if (crop) {
          const f = this.atlas.frame(this.world.cropSpriteAt(crop));
          this.place(b, f, x + 0.5, y + 1, zFor(y + 1));
          if (crop.quality > 0 && crop.stage === 3) {
            const star = this.atlas.frame(crop.quality === 2 ? "star_gold" : "star_silver");
            this.place(b, star, x + 0.25, y + 0.55, zFor(y + 1) + 0.001);
          }
        }
        const prop = this.world.prop(x, y) as Prop;
        if (prop !== Prop.None) {
          const id = this.world.propSprite(prop);
          if (id) {
            const f = this.atlas.frame(id);
            this.place(b, f, x + 0.5, y + 1, zFor(y + 1) + 0.002);
          }
        }
      }
    }
    b.end();
  }

  /** Place a sprite standing on (centreX, baseY) in tile space. */
  private place(
    b: QuadBatch,
    f: Frame,
    centreX: number,
    baseY: number,
    z: number,
    tint: readonly [number, number, number] = WHITE,
    glow = 0,
  ): void {
    const w = f.w / TILE_PX;
    const h = f.h / TILE_PX;
    b.add(centreX - w / 2, -baseY, z, w, h, f, tint, glow);
  }

  private resize(): void {
    const device = this.app.graphicsDevice;
    const h = device.height;
    this.pixelScale = this.forcedScale ?? Math.max(2, Math.round(h / (VIEW_TILES * TILE_PX)));
    const cam = this.camera.camera!;
    cam.orthoHeight = h / (2 * TILE_PX * this.pixelScale);
  }
}

const WHITE: readonly [number, number, number] = [1, 1, 1];

export function zFor(tileY: number): number {
  return tileY * Z_STEP;
}

function clamp(v: number, lo: number, hi: number): number {
  if (hi < lo) return (lo + hi) / 2;
  return Math.min(hi, Math.max(lo, v));
}
